import { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, Trash2, Image as ImageIcon, Loader2, X, Camera } from "lucide-react";
import { toast } from "sonner";
import CameraCapture from "@/components/CameraCapture";

function canManage(user, ticket) {
  if (!user || !ticket) return false;
  if (user.role === "admin" || user.role === "koordinator") return true;
  if (ticket.pic === user.name) return true;
  const tp = ticket.tim_pendamping || [];
  return Array.isArray(tp) && tp.includes(user.name);
}

function PhotoSection({ kind, label, ticket, canUpload, canDelete, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const token = localStorage.getItem("aroma_token");
  const photos = (kind === "before" ? ticket.photos_before : ticket.photos_after) || [];
  const max = 5;

  const url = (p) =>
    `${API_BASE}/tickets/${ticket.id}/photos/${p.id}/download?auth=${encodeURIComponent(token || "")}`;

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/tickets/${ticket.id}/photos?kind=${kind}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      if (photos.length + uploaded >= max) {
        toast.error(`Maksimum ${max} foto ${kind === "before" ? "sebelum" : "sesudah"}`);
        break;
      }
      try {
        await uploadOne(file);
        uploaded++;
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
    setUploading(false);
    if (uploaded > 0) {
      const localized = kind === "before" ? "sebelum" : "sesudah";
      toast.success(`${uploaded} foto ${localized} diunggah`);
      onChange?.();
    }
  };

  const onCameraCapture = async (blob, filename) => {
    if (photos.length >= max) {
      toast.error(`Maksimum ${max} foto ${kind === "before" ? "sebelum" : "sesudah"}`);
      return;
    }
    const file = new File([blob], filename, { type: "image/jpeg" });
    try {
      await uploadOne(file);
      const localized = kind === "before" ? "sebelum" : "sesudah";
      toast.success(`Foto ${localized} tersimpan dari kamera`);
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus foto ini?")) return;
    try {
      await api.delete(`/tickets/${ticket.id}/photos/${id}`);
      toast.success("Foto dihapus");
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="border border-[#E5E7EB] rounded-md p-4" data-testid={`photos-${kind}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF]">
            Foto Bukti
          </div>
          <div className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <Camera className="h-4 w-4 text-[#0055FF]" />
            {label}
            <span className="text-xs font-mono-tab font-normal text-[#9CA3AF]">
              {photos.length}/{max}
            </span>
          </div>
        </div>
        {canUpload && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setCameraOpen(true)}
              disabled={uploading || photos.length >= max}
              className="h-8 px-3 rounded-md bg-[#0A0A0A] hover:bg-[#333] disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"
              data-testid={`photo-camera-${kind}`}
              type="button"
            >
              <Camera className="h-3.5 w-3.5" />
              Kamera
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }}
              data-testid={`photo-input-${kind}`}
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading || photos.length >= max}
              className="h-8 px-3 rounded-md bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"
              data-testid={`photo-upload-${kind}`}
              type="button"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Unggah
            </button>
          </div>
        )}
      </div>

      {photos.length === 0 ? (
        <div className="text-[11px] text-[#9CA3AF] italic text-center py-6 border border-dashed border-[#E5E7EB] rounded-md flex flex-col items-center gap-1">
          <ImageIcon className="h-4 w-4" />
          Belum ada foto {label.toLowerCase()}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative group aspect-square rounded-md overflow-hidden border border-[#E5E7EB] bg-[#F8F9FA]" data-testid={`photo-${p.id}`}>
              <img
                src={url(p)}
                alt={p.original_filename || "foto"}
                className="h-full w-full object-cover cursor-zoom-in"
                onClick={() => setViewer(p)}
              />
              {canDelete && (
                <button
                  onClick={() => remove(p.id)}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/95 hover:bg-white text-[#991B1B] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-sm"
                  data-testid={`photo-del-${p.id}`}
                  aria-label="Hapus"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 text-[9px] text-white/90 font-mono-tab truncate">
                {p.uploaded_by_name}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewer && (
        <div
          className="fixed inset-0 bg-black/85 z-[9999] flex items-center justify-center p-4"
          onClick={() => setViewer(null)}
          data-testid="photo-viewer"
        >
          <button
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            onClick={() => setViewer(null)}
          >
            <X className="h-5 w-5" />
          </button>
          <img src={url(viewer)} alt="" className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs font-mono-tab">
            {viewer.original_filename} · oleh {viewer.uploaded_by_name}
          </div>
        </div>
      )}

      <CameraCapture
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        onCapture={onCameraCapture}
        kindLabel={label}
      />
    </div>
  );
}

export default function TicketPhotos({ ticket, onChange }) {
  const { user, hasPriv } = useAuth();
  const allow = useMemo(() => canManage(user, ticket), [user, ticket]);
  const canUpload = allow && hasPriv("photo_upload");
  const canDelete = allow && hasPriv("photo_delete");
  if (!ticket?.id) return null;
  return (
    <div className="space-y-3">
      <PhotoSection kind="before" label="Sebelum" ticket={ticket} canUpload={canUpload} canDelete={canDelete} onChange={onChange} />
      <PhotoSection kind="after"  label="Sesudah" ticket={ticket} canUpload={canUpload} canDelete={canDelete} onChange={onChange} />
    </div>
  );
}
